/**
 * MODULE: core/auth/frontend/api/authApi.ts
 * PURPOSE: Auth API client – login, logout, refresh, getCurrentUser
 * SIDE_EFFECTS: fetch to API_BASE; handleResponse normalizes backend detail/message for Error (avoids [object Object])
 * Last updated: 2026-02 - handleResponse uses error.detail (string or array) for thrown Error message
 */
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import type {
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  RefreshResponse,
  User,
} from '../../contracts/auth.types';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;

/** Thrown from auth HTTP helpers; includes status for login UX (429, 401, 503). */
export type AuthHttpError = Error & { status: number; retryAfterSeconds?: number };

function parseRetryAfterSeconds(response: Response): number {
  const raw = response.headers.get('Retry-After');
  if (!raw) return 60;
  const asInt = parseInt(raw, 10);
  if (Number.isFinite(asInt) && asInt > 0) return Math.min(asInt, 3600);
  return 60;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const msg =
      (error as { message?: string }).message ??
      (typeof (error as { detail?: string }).detail === 'string'
        ? (error as { detail: string }).detail
        : Array.isArray((error as { detail?: unknown[] }).detail) &&
            (error as { detail: unknown[] }).detail.length > 0
          ? String(
              (error as { detail: { msg?: string }[] }).detail[0]?.msg ??
                (error as { detail: unknown[] }).detail[0]
            )
          : `Request failed (${response.status})`);
    const err = new Error(msg) as AuthHttpError;
    err.status = response.status;
    throw err;
  }
  return response.json();
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export const authApi = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // Trim to avoid accidental whitespace causing 401
    const payload = {
      email: credentials.email.trim().toLowerCase(),
      password: credentials.password,
    };
    const response = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'omit', // Cross-origin: avoid sending cookies; ensures clean CORS
    });
    if (response.status === 429) {
      const err = new Error(
        'Too many sign-in attempts. Please wait before trying again.'
      ) as AuthHttpError;
      err.status = 429;
      err.retryAfterSeconds = parseRetryAfterSeconds(response);
      throw err;
    }
    return handleResponse<LoginResponse>(response);
  },

  async logout(): Promise<void> {
    const response = await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 401) {
      throw new Error('Logout failed');
    }
  },

  async refresh(request: RefreshRequest): Promise<RefreshResponse> {
    const response = await fetch(`${API_BASE}/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handleResponse<RefreshResponse>(response);
  },

  async getCurrentUser(): Promise<User> {
    const response = await fetch(`${API_BASE}/me`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse<User>(response);
  },
};
